/**
 * Data hook for searching users when creating a chat
 * Handles both supplier and buyer user searches
 */

import type {
  SupplierUserSelectOption,
  BuyerUserSelectOption,
} from '@choco-admin/types';
import { useCallback, useMemo } from 'react';

import { useSearchUsersForChatLazyQuery } from '../../../generated';
import { mapToUserSelectOption } from '../utils/createChatMapper';

export interface UseSearchUsersForChatConfig {
  captureException?: (error: Error) => void;
}

export interface UseSearchUsersForChatResponse {
  supplierUsers: SupplierUserSelectOption[];
  buyerUsers: BuyerUserSelectOption[];
  isLoading: boolean;
  error: Error | null;
  searchUsers: (
    searchTerm: string,
    userType: 'supplier' | 'buyer'
  ) => Promise<void>;
  clearResults: () => void;
}

export const useSearchUsersForChat = (
  config: UseSearchUsersForChatConfig = {}
): UseSearchUsersForChatResponse => {
  const { captureException } = config;

  const [executeSearch, { data, loading, error }] =
    useSearchUsersForChatLazyQuery({
      fetchPolicy: 'cache-and-network',
      errorPolicy: 'all',
      notifyOnNetworkStatusChange: true,
    });

  const searchUsers = useCallback(
    async (searchTerm: string, userType: 'supplier' | 'buyer') => {
      if (!searchTerm.trim()) return;

      try {
        const filter: any = {};

        await executeSearch({
          variables: {
            search: searchTerm.trim(),
            limit: 20,
            filter,
          },
        });
      } catch (err) {
        const usersError = err as Error;
        captureException?.(usersError);
        throw usersError;
      }
    },
    [executeSearch, captureException]
  );

  const clearResults = useCallback(() => {
    executeSearch({
      variables: {
        search: '',
        limit: 0,
        filter: {},
      },
    });
  }, [executeSearch]);

  const { supplierUsers, buyerUsers } = useMemo(() => {
    const searchResult = data?.search;

    if (!searchResult?.hits) {
      return { supplierUsers: [], buyerUsers: [] };
    }

    const allUsers =
      searchResult.hits
        ?.filter((hit: any) => hit.entity?.__typename === 'User')
        .map((hit: any) => hit.entity) || [];

    const suppliers: SupplierUserSelectOption[] = [];
    const buyers: BuyerUserSelectOption[] = [];

    allUsers.forEach((user: any) => {
      const isSupplier = Boolean(user.supplier || user.botUser);
      const mappedUser = mapToUserSelectOption(user, isSupplier);

      if (isSupplier) {
        suppliers.push(mappedUser as SupplierUserSelectOption);
      } else {
        buyers.push(mappedUser as BuyerUserSelectOption);
      }
    });

    return {
      supplierUsers: suppliers,
      buyerUsers: buyers,
    };
  }, [data]);

  return {
    supplierUsers,
    buyerUsers,
    isLoading: loading,
    error: error || null,
    searchUsers,
    clearResults,
  };
};
